import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

import apiCalls from "../../../../config/apiCalls";
import fetchMethodRequest from "../../../../config/service";
import showToasterMessage from "../../../UI/ToasterMessage/toasterMessage";
import DoInputField from "../../../Form/Fields/DoInputField";
import DoTextareaField from "../../../Form/Fields/DoTextareaField";
import "./SideBySideLayout.css";
import config from "../../../../config/config";

const AgreementTemplates = ({ templates, fetchTemplates }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("template");

  const [showDialog, setShowDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadUrl = apiCalls.agreementTemplates; // endpoint for file upload

  // React Hook Form for edit mode
  const {
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  // ==========================
  // Auto select first template
  // ==========================
  useEffect(() => {
    if (templates?.length > 0 && !templateId) {
      handleTemplateClick(templates[0]);
    }
  }, [templates, templateId]);

  // ===================
  // NAVIGATION
  // ===================
  const handleTemplateClick = (template) => {
    navigate(`?template=${template._id}`);
  };

  // ===================
  // CLOSE DIALOG
  // ===================
  const handleCloseDialog = () => {
    setShowDialog(false);
    reset();
    setIsEditing(false);
    setSelectedTemplate(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ===================
  // CREATE (FILE UPLOAD)
  // ===================
  function FileUploadButton() {
    const uploadUrl = apiCalls.agreementTemplates;
    const fileInputRef = useRef(null);

    // Handle MULTIPLE files in SINGLE API call
    const handleFileChange = async (event) => {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      event.target.value = null; // Reset input

      try {
        const formData = new FormData();
        files.forEach((file, index) => {
          formData.append("file", file);
        });

        console.log(`Sending ${files.length} files in single request...`);

        // SINGLE API call with all files
        const response = await fetchMethodRequest("POST", uploadUrl, formData);

        console.log("Multi-file response:", response);
        if (response && response.respMessage) {
          showToasterMessage(response.respMessage, "success");
          if (fetchTemplates) await fetchTemplates();
          handleCloseDialog();

          if (response.templateId) {
            navigate(`?template=${response.templateId}`);
          }
        } else {
          throw new Error("Upload failed");
        }
      } catch (err) {
        console.error("Upload error:", err);
        showToasterMessage(`Failed to upload. Please try again.`, "error");
      }
    };

    const handleUploadButtonClick = () => {
      fileInputRef.current?.click();
    };

    return (
      <div className="file-upload-container">
        {/* Hidden multi-file input */}
        <input
          type="file"
          id="file-upload-input"
          multiple
          accept=".pdf,.docx"
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: "none" }}
        />

        {/* Upload button */}
        <Button
          icon="pi pi-upload"
          size="small"
          className="upload-button"
          label="Choose File"
          onClick={handleUploadButtonClick}
        />
      </div>
    );
  }

  // ===================
  // EDIT SUBMIT
  // ===================
  const handleEditSubmit = async (data) => {
    if (!selectedTemplate) return;

    try {
      const url = `${apiCalls.agreementTemplates}/${selectedTemplate._id}`;
      const res = await fetchMethodRequest("PUT", url, {
        fileName: data.name,
      });

      if (res?.respMessage) {
        showToasterMessage(res?.respMessage || "Template updated successfully", "success");
        fetchTemplates?.();
        handleCloseDialog();
      } else {
        showToasterMessage("Failed to update template", "error");
      }
    } catch (err) {
      console.error(err);
      showToasterMessage("Error updating template", "error");
    }
  };

  // ===================
  // EDIT TEMPLATE
  // ===================
  const handleEditTemplate = (template) => {
    console.log(template.fileName, "template")
    setIsEditing(true);
    setSelectedTemplate(template);
    setShowDialog(true);

    setValue("name", template?.fileName);

  };

  // ===================
  // DELETE TEMPLATE
  // ===================
  const handleDeleteTemplate = async (template) => {
    try {
      const url = `${apiCalls.agreementTemplates}/multiDelete`;
      const res = await fetchMethodRequest("POST", url, {
        selectedIds: [template._id],
      });

      if (res?.respCode === 206) {
        showToasterMessage("Template deleted successfully", "success");
        fetchTemplates?.();

        if (templateId === template._id) {
          navigate("/agreementTemplates");
        }
      } else {
        showToasterMessage("Failed to delete template", "error");
      }
    } catch (err) {
      console.error(err);
      showToasterMessage("Error deleting template", "error");
    }
  };

  // ===================
  // OVERLAY
  // ===================
  const showOverlay = (event, template) => {
    setSelectedTemplate(template);
    overlayRef.current.toggle(event);
  };

  const openFile = (template) => {
    const fileUrl = config.imgUrl + template.filePath;
    const extension = template.filePath.split('.').pop()?.toLowerCase();
    let finalUrl = fileUrl;

    if (extension === "docx") {
      finalUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
    }
    window.open(finalUrl, "_blank");
  };
  // ===================
  // RENDER UPLOAD UI (for creation)
  // ===================
  const renderUploadForm = () => (
    <div className="p-3">
      <p className="mb-3">Select a file to upload as a new agreement template.</p>
      <FileUploadButton />
    </div>
  );

  // ===================
  // RENDER EDIT FORM
  // ===================
  const renderEditForm = () => {
    const fields = [
      {
        name: "name",
        type: "text",
        placeholder: "Template Name",
        label: "Template Name",
        required: true,
      },
    ];

    return (
      <form onSubmit={handleSubmit(handleEditSubmit)} className="p-3">
        {fields.map((item) => (
          <div key={item.name} style={{ marginBottom: "15px" }}>
            <Controller
              name={item.name}
              control={control}
              rules={item.required ? { required: `${item.label} is required` } : {}}
              render={({ field, fieldState }) =>
                <DoInputField
                  field={field}              // ✅ PASS field properly
                  fieldState={fieldState}
                  id={field.name}
                  name={field.name}
                  label={item.label}
                  errors={errors}
                  placeholder={item.placeholder}
                />

              }
            />
          </div>
        ))}
        <Button type="submit" className="mt-3">
          Update Template
        </Button>
      </form>
    );
  };

  // ===================
  // JSX RETURN
  // ===================
  return (
    <>
      {/* Sidebar - Template List */}
      <div className="col-md-3 col-lg-3 side-folder-list">
        <div
          className="d-flex justify-content-between align-items-center mb-3 p-3"
          style={{ borderBottom: "3px solid #00356c" }}
        >
          <h3 style={{ color: "#12225f", fontWeight: "bold" }}>
            Agreement Templates
          </h3>
          <Button
            size="small"
            onClick={() => {
              setIsEditing(false);
              setShowDialog(true);
            }}
          >
            <FontAwesomeIcon icon="plus" className="pl-1" size="lg" />
          </Button>
        </div>

        <ul className="folder-list">
          {templates?.map((template) => (
            <li
              key={template._id}
              className={`folder-item-testcase ${templateId === template._id ? "active" : ""
                }`}
              onClick={() => handleTemplateClick(template)}
            >
              <div className="folder-name">
                <span>{template.fileName}</span>
              </div>

              <div
                className="folder-actions"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="options-button"
                  onClick={(e) => showOverlay(e, template)}
                >
                  <i className="pi pi-ellipsis-v"></i>
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Overlay Panel for Edit/Delete */}
      <OverlayPanel ref={overlayRef} className="custom-overlay" dismissable>
        {selectedTemplate && (
          <ul className="overlay-list">
            <li
              className="overlay-list-item"
              onClick={() => {
                handleEditTemplate(selectedTemplate);
                overlayRef.current.hide();
              }}
            >
              <FontAwesomeIcon icon="edit" className="me-2" color="green" />
              Edit
            </li>
            <li
              className="overlay-list-item"
              onClick={() => {
                handleDeleteTemplate(selectedTemplate);
                overlayRef.current.hide();
              }}
            >
              <i className="pi pi-trash me-2"></i> Delete
            </li>
            <li
              className="overlay-list-item"
              onClick={() => {
                openFile(selectedTemplate);
                overlayRef.current.hide();
              }}
            >
              <i className="pi pi-eye me-2"></i> View Original File
            </li>
          </ul>
        )}
      </OverlayPanel>

      {/* Dialog - conditional content */}
      <Dialog
        header={isEditing ? "Edit Template" : "Upload Agreement Template"}
        visible={showDialog}
        style={{ width: "30vw" }}
        modal
        draggable={false}
        onHide={handleCloseDialog}
      >
        {isEditing ? renderEditForm() : renderUploadForm()}
      </Dialog>
    </>
  );
};

export default AgreementTemplates;